/**
 * 统一的认证错误处理工具
 * 处理401错误、token刷新、用户登出等认证相关逻辑
 */

import authApi from '../services/authApi';

// 使用相对路径，通过Nginx代理访问后端API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 防止重复刷新token的锁 & 失败后的降噪标记
let isRefreshing = false;
let refreshPromise = null;
let refreshDisabled = false; // 一旦检测到后端返回401（无refresh_token等），当前页面生命周期内不再尝试刷新
let failedQueue = []; // 存储在刷新期间失败的请求，等待刷新完成后重试
let isHandlingAuthError = false; // 防止 handleAuthError 重复调用 logout

/**
 * 处理队列中的请求
 * @param {Error|null} error - 如果有错误，所有请求都会失败；否则都会成功
 * @param {string|null} token - 新的token（如果刷新成功）
 */
function processQueue(error, token = null) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
}

/**
 * 重置刷新状态（在用户重新登录时调用）
 */
export function resetRefreshState() {
  refreshDisabled = false;
  isRefreshing = false;
  refreshPromise = null;
}

/**
 * 尝试刷新access token
 * @returns {Promise<boolean>} 刷新是否成功
 */
export async function tryRefreshToken() {
  // 如果当前页面已经确认无法刷新（例如后端返回过一次401），直接短路，避免重复打后端
  if (refreshDisabled) {
    return false;
  }
  // 如果正在刷新，等待当前的刷新完成
  if (isRefreshing && refreshPromise) {
    try {
      await refreshPromise;
      return true;
    } catch (error) {
      return false;
    }
  }

  // 开始刷新
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: isSecure ? 'include' : 'same-origin', // 仅在安全环境下包含cookie
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // refresh_token无效或过期，标记为禁用
          console.warn('[Auth] Token刷新失败: refresh_token无效或过期 (401)');
          refreshDisabled = true;
          return false;
        }
        // 其他HTTP错误（如500），不标记为禁用，可能是临时错误
        console.error(`[Auth] Token刷新失败: HTTP ${response.status}`);
        throw new Error(`刷新令牌失败: ${response.status}`);
      }

      // 尝试解析响应
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // 响应不是有效的JSON，可能是服务器错误
        console.error('[Auth] Token刷新响应解析失败:', parseError);
        throw new Error('刷新令牌响应格式错误');
      }
      
      if (data?.success && data?.access_token) {
        localStorage.setItem('access_token', data.access_token);
        // 刷新成功，重置禁用标志（允许后续刷新）
        refreshDisabled = false;
        return true;
      }

      // 响应格式正确但没有token，可能是业务逻辑错误
      console.warn('[Auth] Token刷新失败: 响应中缺少access_token');
      return false;
    } catch (error) {
      // 网络错误或其他异常，不标记为禁用（可能是临时问题）
      // 只有明确的401错误才会禁用刷新
      console.error('[Auth] Token刷新异常:', error.message);
      if (error.message && error.message.includes('401')) {
        refreshDisabled = true;
      }
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  try {
    return await refreshPromise;
  } catch (error) {
    console.error('[Auth] Token刷新Promise异常:', error);
    return false;
  }
}

/**
 * 处理认证错误
 * 清除用户信息并跳转到登录页（不再弹出“登录已过期”提示框，直接跳转更简洁）
 * @param {string} message - 错误消息（仅用于日志）
 * @param {boolean} showAlert - 保留兼容参数（目前不再使用弹窗）
 * @param {boolean} redirectToLogin - 是否跳转到登录页
 */
export async function handleAuthError(message = '登录已过期，请重新登录', showAlert = false, redirectToLogin = true) {
  console.log(`[Auth] 处理认证错误: ${message}, redirectToLogin=${redirectToLogin}`);
  
  // 防止重复处理认证错误
  if (isHandlingAuthError) {
    return;
  }
  
  isHandlingAuthError = true;
  
  // 重置刷新状态，允许重新登录后可以刷新token
  resetRefreshState();
  
  try {
    // 调用后端登出API，清除cookie中的refresh_token
    await authApi.logout();
  } catch (error) {
    // 即使API调用失败，也继续清理本地存储
    console.warn('[Auth] 登出API调用失败，继续清理本地存储:', error);
  }
  
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('token');
  
  // 清除其他相关状态
  localStorage.removeItem('searchLastVisit');
  localStorage.removeItem('currentSessionId');
  localStorage.removeItem('searchLastUserId');
  localStorage.removeItem('chatSessions');
  localStorage.removeItem('jobSearchResults');
  localStorage.removeItem('currentSearchQuery');
  localStorage.removeItem('searchResults');
  localStorage.removeItem('searchQuery');

  // 直接跳转到用户登录主页（例如 http://localhost:3000/ 的首页）
  // 避免弹窗，用户体验更干净
  if (redirectToLogin && !window.location.pathname.startsWith('/admin')) {
    setTimeout(() => {
      window.location.href = '/';
    }, 50);
  }
  
  // 重置标志，允许下次处理认证错误
  isHandlingAuthError = false;
}

/**
 * 检查响应并处理认证错误
 * @param {Response} response - fetch响应对象
 * @param {boolean} showAlert - 是否显示alert
 * @returns {boolean} 是否是认证错误
 */
export async function checkAndHandleAuthError(response, showAlert = false) {
  if (response.status === 401) {
    // 尝试刷新token
    const refreshSuccess = await tryRefreshToken();
    
    if (!refreshSuccess) {
      // 刷新失败，处理认证错误
      await handleAuthError('登录已过期，请重新登录', showAlert, true);
      return true;
    }
    
    // 刷新成功，返回false表示不是致命错误，可以重试请求
    return false;
  }
  
  return false;
}

/**
 * 处理Axios的401错误
 * 自动尝试刷新token并重试原始请求
 * 使用请求队列机制避免并发刷新
 * @param {Error} error - Axios错误对象
 * @returns {Promise} 重试后的响应或拒绝的Promise
 */
export async function handleAxiosAuthError(error) {
  // 检查是否是401错误
  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  const url = error.config?.url || 'unknown';

  if (url.includes('/auth/logout')) {
    await handleAuthError('登录已过期，请重新登录', false, true);
    return Promise.reject(error);
  }

  // 检查原始请求配置
  const originalRequest = error.config;

  // 如果已经重试过，直接处理认证错误
  if (originalRequest._retry) {
    console.warn(`[Auth] 请求已重试过仍失败: ${url}`);
    await handleAuthError('登录已过期，请重新登录', false, true);
    return Promise.reject(error);
  }

  // 标记为已重试
  originalRequest._retry = true;

  // 如果正在刷新token，将此请求加入队列
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
    .then(token => {
      // 刷新成功，更新token并重试请求
      if (token && originalRequest.headers) {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
      }
      const axios = require('axios').default || require('axios');
      return axios(originalRequest);
    })
    .catch(err => {
      console.error(`[Auth] 队列中的请求重试失败: ${url}`, err);
      return Promise.reject(err);
    });
  }

  // 尝试刷新token
  const refreshSuccess = await tryRefreshToken();

  if (!refreshSuccess) {
    console.warn(`[Auth] Token刷新失败，处理队列(${failedQueue.length}个请求)`);
    // 刷新失败，处理队列中的所有请求
    processQueue(error, null);
    // 刷新失败，处理认证错误
    await handleAuthError('登录已过期，请重新登录', false, true);
    return Promise.reject(error);
  }

  console.log(`[Auth] Token刷新成功，处理队列(${failedQueue.length}个请求)并重试当前请求`);
  // 刷新成功，处理队列中的所有请求
  const newToken = localStorage.getItem('access_token');
  processQueue(null, newToken);
  
  // 更新当前请求的token并重试
  if (newToken && originalRequest.headers) {
    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
  }

  // 重试原始请求
  try {
    const axios = (await import('axios')).default;
    const retryResponse = await axios(originalRequest);
    return retryResponse;
  } catch (retryError) {
    // 重试仍然失败，检查是否是401错误
    console.error(`[Auth] 请求重试失败: ${url}`, retryError);
    if (retryError.response?.status === 401) {
      await handleAuthError('登录已过期，请重新登录', false, true);
    }
    return Promise.reject(retryError);
  }
}

